import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const formatError = (detail) => {
  if (!detail) return "A apărut o eroare.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (typeof e?.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  }
  return String(detail);
};

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Autentificare reușită");
      navigate("/admin");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Autentificare eșuată");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4 grid-overlay"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center h-12 w-12 bg-yellow-500 text-black font-display font-black mb-6">
            L
          </div>
          <div className="overline mb-3">ZONĂ ADMINISTRATOR</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
            Autentificare
          </h1>
          <p className="mt-3 text-sm text-neutral-400">
            Acces restricționat. Doar pentru expertul autorizat.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="bg-[#0d0d0d] border border-neutral-800 p-8 space-y-5"
        >
          <div>
            <label className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
              Email *
            </label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full bg-transparent border border-neutral-800 px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-yellow-500 transition-colors text-sm"
            />
          </div>
          <div>
            <label className="block font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
              Parolă *
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full bg-transparent border border-neutral-800 px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-yellow-500 transition-colors text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-3 btn-yellow px-6 py-3.5 text-sm font-mono-tech uppercase tracking-[0.15em] disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Se autentifică...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4" /> Intră în panou <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a
            href="/"
            data-testid="back-to-site"
            className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-neutral-500 hover:text-yellow-400 transition-colors"
          >
            ← Înapoi la site
          </a>
        </div>
      </div>
    </div>
  );
}
